import React, { useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useLang from "../../hooks/useLang";
import test1Bg from "../../assets/test-1.png";
import dataMigrationBg from "../../assets/data-migration.png";
import dataCloudBg from "../../assets/data-cloud.png";
import bankBg from "../../assets/bank.png";
import bullBg from "../../assets/bull.png";

interface ServiceCard {
  number: string;
  category: string;
  title: string;
  slug?: string;
  tags: string[];
  bgImage?: string;
  solidBg?: string;
}

const CARD_IMAGES = [
  { bgImage: test1Bg },
  {
    solidBg: "linear-gradient(140deg, #cfdfe0 0%, #b8cdd0 100%)",
    bgImage: bankBg,
  },
  { bgImage: dataMigrationBg },
  {
    solidBg: "linear-gradient(140deg, #ece0c4 0%, #d9c9a8 100%)",
    bgImage: bullBg,
  },
  { bgImage: dataCloudBg },
];

interface CropInfo {
  W: number;
  H: number;
  cropX: number;
  cropY: number;
  cropW: number;
  cropH: number;
  dpr: number;
}

const RibbedGlassCanvas: React.FC<{ src: string; isHovered: boolean }> = ({
  src,
  isHovered,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const cropRef = useRef<CropInfo | null>(null);
  const dispRef = useRef(1); // 1 = full ribbed, 0 = clear
  const rafRef = useRef(0);

  const draw = useRef((disp: number) => {
    const canvas = canvasRef.current;
    const cr = cropRef.current;
    const img = imgRef.current;
    if (!canvas || !cr || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { W, H, cropX, cropY, cropW, cropH, dpr } = cr;
    ctx.clearRect(0, 0, W, H);

    const RIB = 18 * dpr;
    const MAX_DISP = RIB * 0.62 * disp;
    const sliceW = cropW / W;

    for (let x = 0; x < W; x++) {
      const phase = (x % RIB) / RIB;
      const dx = Math.sin(phase * Math.PI * 2) * MAX_DISP;
      const frac = Math.max(0, Math.min(1, (x + dx) / W));
      ctx.drawImage(
        img,
        cropX + frac * cropW,
        cropY,
        sliceW,
        cropH,
        x,
        0,
        1,
        H,
      );
    }

    if (disp > 0.01) {
      for (let x = 0; x < W; x += RIB) {
        const hl = ctx.createLinearGradient(x, 0, x + RIB, 0);
        hl.addColorStop(0, "rgba(255,255,255,0)");
        hl.addColorStop(0.08, `rgba(255,255,255,${0.22 * disp})`);
        hl.addColorStop(0.22, `rgba(255,255,255,${0.04 * disp})`);
        hl.addColorStop(0.5, `rgba(0,0,0,${0.06 * disp})`);
        hl.addColorStop(0.72, `rgba(255,255,255,${0.04 * disp})`);
        hl.addColorStop(0.88, `rgba(255,255,255,${0.18 * disp})`);
        hl.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = hl;
        ctx.fillRect(x, 0, RIB, H);
      }
    }
  }).current;

  // Setup + resize
  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const setup = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = wrap.getBoundingClientRect();
      const W = Math.round(rect.width * dpr);
      const H = Math.round(rect.height * dpr);
      if (!W || !H) return;
      canvas.width = W;
      canvas.height = H;

      const img = new Image();
      img.src = src;
      imgRef.current = img;

      const onLoad = () => {
        const ia = img.naturalWidth / img.naturalHeight;
        const ca = W / H;
        let cropW: number, cropH: number, cropX: number, cropY: number;
        if (ia > ca) {
          cropH = img.naturalHeight;
          cropW = cropH * ca;
          cropX = (img.naturalWidth - cropW) / 2;
          cropY = 0;
        } else {
          cropW = img.naturalWidth;
          cropH = cropW / ca;
          cropX = 0;
          cropY = (img.naturalHeight - cropH) / 2;
        }
        cropRef.current = { W, H, cropX, cropY, cropW, cropH, dpr };
        draw(dispRef.current);
      };

      if (img.complete && img.naturalWidth > 0) onLoad();
      else img.onload = onLoad;
    };

    setup();
    window.addEventListener("resize", setup);
    return () => window.removeEventListener("resize", setup);
  }, [src, draw]);

  // Hover animation
  useEffect(() => {
    const target = isHovered ? 0 : 1;
    cancelAnimationFrame(rafRef.current);

    const animate = () => {
      const cur = dispRef.current;
      const diff = target - cur;
      if (Math.abs(diff) < 0.008) {
        dispRef.current = target;
        draw(target);
        return;
      }
      dispRef.current = cur + diff * 0.1;
      draw(dispRef.current);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [isHovered, draw]);

  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0 }}>
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
};

const NavArrow: React.FC<{ dir: "left" | "right" }> = ({ dir }) => (
  <Box
    component="svg"
    sx={{ width: 22, height: 22, display: "block" }}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
  >
    {dir === "left" ? (
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <path
        d="M5 12h14M12 5l7 7-7 7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </Box>
);

const DigitalTransformation: React.FC = () => {
  const { t } = useTranslation();
  const heading = t("digitalTransformation.heading");
  const subtitle = t("digitalTransformation.subtitle");
  const cards = (
    t("digitalTransformation.cards", { returnObjects: true }) as Array<{
      number: string;
      category: string;
      title: string;
      tags: string[];
    }>
  ).map((c, i) => ({ ...c, ...CARD_IMAGES[i] })) as ServiceCard[];

  const navigate = useNavigate();
  const lang = useLang();
  const scrollRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [dynamicPadding, setDynamicPadding] = useState<number | string>(
    "calc(max(48px, (100vw - 1280px) / 2 + 48px))",
  );
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  useEffect(() => {
    const updatePadding = () => {
      if (titleRef.current) {
        const left = titleRef.current.getBoundingClientRect().left;
        if (left > 0) {
          setDynamicPadding(`${left}px`);
        }
      }
    };
    updatePadding();
    setTimeout(updatePadding, 100);
    window.addEventListener("resize", updatePadding);
    return () => window.removeEventListener("resize", updatePadding);
  }, []);

  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);
  const dragDistance = useRef(0);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    isDragging.current = true;
    dragDistance.current = 0;
    scrollRef.current.style.scrollSnapType = "none";
    scrollRef.current.style.scrollBehavior = "auto";
    startX.current = e.pageX - scrollRef.current.offsetLeft;
    scrollLeft.current = scrollRef.current.scrollLeft;
  };

  const stopDragging = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (scrollRef.current) {
      scrollRef.current.style.scrollSnapType = "x mandatory";
      scrollRef.current.style.scrollBehavior = "smooth";
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX.current) * 1.5;
    dragDistance.current = Math.abs(walk);
    scrollRef.current.scrollLeft = scrollLeft.current - walk;
  };

  const handleCardClick = (index: number, slug?: string) => {
    if (dragDistance.current > 5) return;
    if (slug) {
      navigate(`/${lang}/projects/${slug}`);
      return;
    }
    window.dispatchEvent(new CustomEvent("capability:open", { detail: index }));
    const section = document.getElementById("solutions");
    if (section) {
      const navbarHeight =
        (document.querySelector("nav") as HTMLElement)?.offsetHeight ?? 80;
      const top =
        section.getBoundingClientRect().top +
        window.scrollY -
        navbarHeight -
        24;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const scrollByAmount = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      const scrollAmount =
        direction === "left" ? -clientWidth / 1.1 : clientWidth / 1.1;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <Box
      component="section"
      id="projects"
      sx={{
        bgcolor: "#f8f9fb",
        overflow: "hidden",
        position: "relative",
        py: { xs: 8, md: 12 },
      }}
    >
      {/* Border lines */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          bgcolor: "rgba(29, 47, 65, 0.12)",
          zIndex: 1,
        }}
      />

      {/* Heading row */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1280,
          mx: "auto",
          pl: { xs: 3, md: "40px" },
          pr: { xs: 3, md: 6 },
          mb: { xs: 5, md: 8 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "flex-end" },
          justifyContent: "space-between",
          gap: 4,
        }}
      >
        <motion.div
          ref={titleRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              pl: { xs: 2.5, md: 3.5 },
              pt: { xs: 2.5, md: 3.5 },
              pr: { xs: 2.5, md: 3.5 },
              pb: { xs: 2.5, md: 3.5 },
            }}
          >
            {/* Top-left corner */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: { xs: 48, md: 72 },
                height: "1px",
                bgcolor: "rgba(29,47,65,0.2)",
                pointerEvents: "none",
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "1px",
                height: { xs: 48, md: 72 },
                bgcolor: "rgba(29,47,65,0.2)",
                pointerEvents: "none",
              }}
            />
            {/* Bottom-right corner */}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: { xs: 48, md: 72 },
                height: "1px",
                bgcolor: "rgba(29,47,65,0.2)",
                pointerEvents: "none",
              }}
            />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: "1px",
                height: { xs: 48, md: 72 },
                bgcolor: "rgba(29,47,65,0.2)",
                pointerEvents: "none",
              }}
            />
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.8rem" },
                fontWeight: 700,
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
                color: "#1D2F41",
                fontFamily: '"Segoe UI Semibold", "Segoe UI", sans-serif',
              }}
            >
              {heading}
            </Typography>
            <Typography
              sx={{
                mt: { xs: 1.5, md: 2 },
                maxWidth: 420,
                fontSize: { xs: "0.85rem", md: "0.95rem" },
                fontWeight: 300,
                lineHeight: 1.6,
                color: "rgba(29, 47, 65, 0.55)",
                fontFamily: '"Calibri", sans-serif',
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        </motion.div>
      </Box>

      {/* Horizontal Scroll Track */}
      <Box
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={stopDragging}
        onMouseUp={stopDragging}
        onMouseMove={handleMouseMove}
        sx={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          gap: { xs: 3, md: 4 },
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          overscrollBehaviorX: "none",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
          paddingLeft: { xs: "24px", md: dynamicPadding },
          paddingRight: { xs: "24px", md: dynamicPadding },
          scrollPaddingLeft: { xs: "24px", md: dynamicPadding },
          scrollPaddingRight: { xs: "24px", md: dynamicPadding },
          pb: 8,
          pt: 4,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
        }}
      >
        {cards.map((card, i) => (
          <motion.div
            key={card.number}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{
              duration: 0.8,
              delay: i * 0.15,
              ease: [0.16, 0.77, 0.47, 0.97],
            }}
          >
            <Box
              className="card-container"
              component={card.slug ? Link : "div"}
              to={card.slug ? `/${lang}/projects/${card.slug}` : undefined}
              onClick={(e: React.MouseEvent) => {
                if (dragDistance.current > 5) {
                  e.preventDefault();
                  return;
                }
                if (!card.slug) handleCardClick(i, undefined);
              }}
              onMouseEnter={() => setHoveredCard(i)}
              onMouseLeave={() => setHoveredCard(null)}
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                minWidth: { xs: "85vw", md: "440px" },
                height: { xs: "450px", md: "540px" },
                scrollSnapAlign: "start",
                position: "relative",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                bgcolor: card.bgImage ? "transparent" : card.solidBg,
                border: "1px solid rgba(29,47,65,0.08)",
                transition:
                  "box-shadow 0.5s cubic-bezier(0.25, 1, 0.5, 1), transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)",
                "&:hover": {
                  boxShadow: "0 24px 48px -12px rgba(29,47,65,0.15)",
                  transform: "translateY(-6px)",
                },
              }}
            >
              {/* Ribbed glass canvas (distortion + specular) */}
              {card.bgImage && (
                <RibbedGlassCanvas
                  src={card.bgImage}
                  isHovered={hoveredCard === i}
                />
              )}

              {/* Gradient Overlay */}
              {card.bgImage && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(to bottom, rgba(13,26,39,0.1) 0%, rgba(13,26,39,0.4) 40%, rgba(13,26,39,0.95) 100%)",
                    zIndex: 1,
                  }}
                />
              )}

              {/* Arrow indicator - only for project cards, visible on hover */}
              {card.slug && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 24,
                    right: 24,
                    zIndex: 4,
                    width: 36,
                    height: 36,
                    border: `1px solid ${card.bgImage ? "rgba(255,255,255,0.4)" : "rgba(29,47,65,0.25)"}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: card.bgImage ? "#ffffff" : "#1D2F41",
                    fontSize: "1rem",
                    opacity: 0,
                    transform: "translate(6px, -6px)",
                    transition:
                      "opacity 0.35s ease, transform 0.35s cubic-bezier(0.16, 0.77, 0.47, 0.97)",
                    ".card-container:hover &": {
                      opacity: 1,
                      transform: "translate(0px, 0px)",
                    },
                  }}
                >
                  <ArrowUpRight size={18} strokeWidth={1.5} />
                </Box>
              )}

              {/* Content Bottom */}
              <Box
                sx={{
                  position: "relative",
                  zIndex: 3,
                  p: { xs: 4, md: 5 },
                  width: "100%",
                  boxSizing: "border-box",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    color: card.bgImage
                      ? "rgba(255,255,255,0.8)"
                      : "rgba(29,47,65,0.6)",
                    fontFamily: '"Calibri", sans-serif',
                    mb: 2,
                    display: "block",
                  }}
                >
                  {card.category}
                </Typography>

                <Typography
                  sx={{
                    fontSize: { xs: "1.5rem", md: "1.85rem" },
                    fontWeight: 400,
                    letterSpacing: "-0.02em",
                    lineHeight: 1.2,
                    color: card.bgImage ? "#ffffff" : "#1D2F41",
                    fontFamily: '"Segoe UI", sans-serif',
                    mb: 4,
                  }}
                >
                  {card.title}
                </Typography>

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {card.tags.map((tag) => (
                    <Box
                      key={tag}
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        fontFamily: '"Calibri", sans-serif',
                        color: card.bgImage
                          ? "rgba(255,255,255,0.9)"
                          : "rgba(29,47,65,0.8)",
                        border: `1px solid ${card.bgImage ? "rgba(255,255,255,0.3)" : "rgba(29,47,65,0.2)"}`,
                        px: 2,
                        py: 0.75,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        backdropFilter: "blur(4px)",
                        transition: "all 0.3s ease",
                        ".card-container:hover &": {
                          bgcolor: card.bgImage
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(29,47,65,0.05)",
                        },
                      }}
                    >
                      {tag}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 1280,
          mx: "auto",
          px: { xs: 3, md: 6 },
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
        }}
      >
        <Box
          component="button"
          onClick={() => scrollByAmount("left")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: 0,
            border: "1px solid rgba(29, 47, 65, 0.2)",
            bgcolor: "transparent",
            cursor: "pointer",
            color: "#1D2F41",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "#1D2F41",
              borderColor: "#1D2F41",
              color: "#f8f9fb",
            },
            "&:active": { transform: "scale(0.95)" },
          }}
        >
          <NavArrow dir="left" />
        </Box>
        <Box
          component="button"
          onClick={() => scrollByAmount("right")}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 56,
            height: 56,
            borderRadius: 0,
            border: "1px solid rgba(29, 47, 65, 0.2)",
            bgcolor: "transparent",
            cursor: "pointer",
            color: "#1D2F41",
            transition: "all 0.3s ease",
            "&:hover": {
              bgcolor: "#1D2F41",
              borderColor: "#1D2F41",
              color: "#f8f9fb",
            },
            "&:active": { transform: "scale(0.95)" },
          }}
        >
          <NavArrow dir="right" />
        </Box>
      </Box>
    </Box>
  );
};

export default DigitalTransformation;
